/*****************************************************************
 *  CUSTOMER BUTLER - THE BOX FOR EDITING ONE FIELD                *
 *  File: apps/web/src/pages/customer/FieldEditor.js               *
 *  What it does: draws the right input for one field's data type  *
 *  (text, long text, email, phone, number, date, date and time,   *
 *  yes/no, one choice, many choices, tags). Picklists keep        *
 *  Zoho's values in Zoho's order.                                 *
 *  Reads: nothing.  Writes: nothing (it hands the value up).      *
 *****************************************************************/

import React from 'react';
import { toLocalInputValue, fromLocalInputValue } from './customerDates';

/*****************************************************************
 *  SECTION 1 - THE SMALL INPUTS                                   *
 *****************************************************************/

const ZOHO_EMPTY_CHOICE = '-None-'; // Zoho's "nothing picked"; Butler shows it as an empty choice

/* ---- OneChoiceBox -- a drop-down for one choice ----------------
   allowNothing: a custom field may be left empty; a Butler built-in
   choice (status, customer type) always holds one of its words.  */
function OneChoiceBox({ inputId, choices, value, onChange, allowNothing }) {
  const realChoices = choices.filter((oneChoice) => oneChoice !== ZOHO_EMPTY_CHOICE);

  return (
    <select id={inputId} className="cb-select" value={value} onChange={(changeEvent) => onChange(changeEvent.target.value)}>
      {allowNothing ? <option value="">Nothing picked</option> : null}
      {realChoices.map((oneChoice) => (
        <option key={oneChoice} value={oneChoice}>{oneChoice}</option>
      ))}
    </select>
  );
}

/* ---- ManyChoiceBoxes -- tick boxes for several choices ---- */
function ManyChoiceBoxes({ choices, value, onChange }) {
  const pickedChoices = Array.isArray(value) ? value : [];

  function flipChoice(oneChoice, isTicked) {
    const withoutIt = pickedChoices.filter((pickedOne) => pickedOne !== oneChoice);
    const nextPicked = isTicked ? withoutIt.concat([oneChoice]) : withoutIt;

    onChange(nextPicked);
  }

  return (
    <div className="cb-choices">
      {choices.map((oneChoice) => (
        <label key={oneChoice} className="cb-choice">
          <input
            type="checkbox"
            checked={pickedChoices.includes(oneChoice)}
            onChange={(changeEvent) => flipChoice(oneChoice, changeEvent.target.checked)}
          />
          {oneChoice}
        </label>
      ))}
    </div>
  );
}

/* ---- TagsBox -- tags typed as one line, split on commas ---- */
function TagsBox({ inputId, value, onChange }) {
  const tagLine = Array.isArray(value) ? value.join(', ') : '';

  function splitTags(typedLine) {
    const typedTags = typedLine.split(',').map((oneTag) => oneTag.trim());

    onChange(typedTags);
  }

  return (
    <input
      id={inputId}
      className="cb-input"
      value={tagLine}
      placeholder="Tags, split by commas"
      onChange={(changeEvent) => splitTags(changeEvent.target.value)}
    />
  );
}

/*****************************************************************
 *  SECTION 2 - THE EDITOR                                         *
 *****************************************************************/

/* ---- FieldEditor({ screenField, value, onChange, inputId }) ----
   In: the field; the value being edited (see fieldValues.
   startingEditValue); a change handler; the id the label points
   at. Out: the input. A date-and-time box works in the viewer's
   local time and hands up a true ISO moment.                    */
export function FieldEditor({ screenField, value, onChange, inputId }) {
  const dataType = screenField.dataType;

  if (dataType === 'textarea') {
    return <textarea id={inputId} className="cb-textarea" value={value} onChange={(changeEvent) => onChange(changeEvent.target.value)} />;
  }

  if (dataType === 'dropdown') {
    const allowNothing = screenField.kind !== 'builtin';

    return (
      <OneChoiceBox
        inputId={inputId}
        choices={screenField.choices || []}
        value={value}
        onChange={onChange}
        allowNothing={allowNothing}
      />
    );
  }

  if (dataType === 'multiselect') {
    return <ManyChoiceBoxes choices={screenField.choices || []} value={value} onChange={onChange} />;
  }

  if (dataType === 'tags') {
    return <TagsBox inputId={inputId} value={value} onChange={onChange} />;
  }

  if (dataType === 'boolean') {
    return (
      <label className="cb-choice">
        <input id={inputId} type="checkbox" checked={value === true} onChange={(changeEvent) => onChange(changeEvent.target.checked)} />
        Yes
      </label>
    );
  }

  if (dataType === 'date') {
    return <input id={inputId} className="cb-input" type="date" value={value} onChange={(changeEvent) => onChange(changeEvent.target.value)} />;
  }

  if (dataType === 'datetime') {
    return (
      <input
        id={inputId}
        className="cb-input"
        type="datetime-local"
        value={toLocalInputValue(value)}
        onChange={(changeEvent) => onChange(fromLocalInputValue(changeEvent.target.value))}
      />
    );
  }

  const boxTypeForData = {
    email: 'email',
    phone: 'tel',
    number: 'number',
    decimal: 'number',
    currency: 'number',
    url: 'url',
  };

  const boxType = boxTypeForData[dataType] || 'text';

  return <input id={inputId} className="cb-input" type={boxType} value={value} onChange={(changeEvent) => onChange(changeEvent.target.value)} />;
}

export default FieldEditor;
